import { createSlice, createAction, PayloadAction } from '@reduxjs/toolkit';
import { globalReset } from '@/store/actions';
import {
    GroupCollection,
    CreateGroupCollectionRequest,
    UpdateGroupCollectionRequest,
    PaginationParams,
} from '@/services/vocabularyService';

// === GROUP COLLECTIONS ACTIONS ===
export const fetchGroupCollectionsRequest = createAction<PaginationParams | undefined>(
    'groupCollections/fetchGroupCollectionsRequest',
);
export const fetchGroupCollectionsSuccess = createAction<GroupCollection[]>(
    'groupCollections/fetchGroupCollectionsSuccess',
);
export const fetchGroupCollectionsFailure = createAction<string>('groupCollections/fetchGroupCollectionsFailure');

export const createGroupCollectionRequest = createAction<CreateGroupCollectionRequest>(
    'groupCollections/createGroupCollectionRequest',
);
export const createGroupCollectionSuccess = createAction<GroupCollection>(
    'groupCollections/createGroupCollectionSuccess',
);
export const createGroupCollectionFailure = createAction<string>('groupCollections/createGroupCollectionFailure');

export const updateGroupCollectionRequest = createAction<UpdateGroupCollectionRequest>(
    'groupCollections/updateGroupCollectionRequest',
);
export const updateGroupCollectionSuccess = createAction<GroupCollection>(
    'groupCollections/updateGroupCollectionSuccess',
);
export const updateGroupCollectionFailure = createAction<string>('groupCollections/updateGroupCollectionFailure');

export const deleteGroupCollectionRequest = createAction<string>('groupCollections/deleteGroupCollectionRequest');
export const deleteGroupCollectionSuccess = createAction<string>('groupCollections/deleteGroupCollectionSuccess');
export const deleteGroupCollectionFailure = createAction<string>('groupCollections/deleteGroupCollectionFailure');

// === STATE INTERFACE ===
export interface GroupCollectionsState {
    groupCollections: GroupCollection[];
    selectedGroupCollection: GroupCollection | null;

    // Loading States
    isLoadingCollections: boolean;
    isCreatingCollection: boolean;
    isUpdatingCollection: boolean;
    isDeletingCollection: boolean;

    // Error States
    collectionError: string | null;
}

const initialState: GroupCollectionsState = {
    groupCollections: [],
    selectedGroupCollection: null,
    isLoadingCollections: false,
    isCreatingCollection: false,
    isUpdatingCollection: false,
    isDeletingCollection: false,
    collectionError: null,
};

const groupCollectionsSlice = createSlice({
    name: 'groupCollections',
    initialState,
    reducers: {
        setSelectedGroupCollection: (state, action: PayloadAction<GroupCollection | null>) => {
            state.selectedGroupCollection = action.payload;
        },
        clearCollectionError: (state) => {
            state.collectionError = null;
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(globalReset, () => initialState)

            // === FETCH GROUP COLLECTIONS ===
            .addCase(fetchGroupCollectionsRequest, (state) => {
                state.isLoadingCollections = true;
                state.collectionError = null;
            })
            .addCase(fetchGroupCollectionsSuccess, (state, action) => {
                state.isLoadingCollections = false;
                state.groupCollections = action.payload;
            })
            .addCase(fetchGroupCollectionsFailure, (state, action) => {
                state.isLoadingCollections = false;
                state.collectionError = action.payload;
            })

            // === CREATE GROUP COLLECTION ===
            .addCase(createGroupCollectionRequest, (state) => {
                state.isCreatingCollection = true;
                state.collectionError = null;
            })
            .addCase(createGroupCollectionSuccess, (state, action) => {
                state.isCreatingCollection = false;
                state.groupCollections.unshift(action.payload);
            })
            .addCase(createGroupCollectionFailure, (state, action) => {
                state.isCreatingCollection = false;
                state.collectionError = action.payload;
            })

            // === UPDATE GROUP COLLECTION ===
            .addCase(updateGroupCollectionRequest, (state) => {
                state.isUpdatingCollection = true;
                state.collectionError = null;
            })
            .addCase(updateGroupCollectionSuccess, (state, action) => {
                state.isUpdatingCollection = false;
                const index = state.groupCollections.findIndex((collection) => collection.id === action.payload.id);
                if (index !== -1) {
                    state.groupCollections[index] = action.payload;
                }
                if (state.selectedGroupCollection?.id === action.payload.id) {
                    state.selectedGroupCollection = action.payload;
                }
            })
            .addCase(updateGroupCollectionFailure, (state, action) => {
                state.isUpdatingCollection = false;
                state.collectionError = action.payload;
            })

            // === DELETE GROUP COLLECTION ===
            .addCase(deleteGroupCollectionRequest, (state) => {
                state.isDeletingCollection = true;
                state.collectionError = null;
            })
            .addCase(deleteGroupCollectionSuccess, (state, action) => {
                state.isDeletingCollection = false;
                state.groupCollections = state.groupCollections.filter(
                    (collection) => collection.id !== action.payload,
                );
                if (state.selectedGroupCollection?.id === action.payload) {
                    state.selectedGroupCollection = null;
                }
            })
            .addCase(deleteGroupCollectionFailure, (state, action) => {
                state.isDeletingCollection = false;
                state.collectionError = action.payload;
            });
    },
});

export const { setSelectedGroupCollection, clearCollectionError } = groupCollectionsSlice.actions;

export default groupCollectionsSlice.reducer;
